/** Fiche détail maison (V1 minimale) — alimentée par l'item de la liste. */
import { Link } from '../lib/router';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/PageHeader';
import { BocuseImage } from '../components/BocuseImage';
import { useMaison } from '../lib/useMaisons';
import { dayLabel, formatSlots } from '../lib/hours';
import './MaisonDetailScreen.css';

export function MaisonDetailScreen({ id }: { id: string }) {
  const { t, lang } = useI18n();
  const { maison, loading, error } = useMaison(id);

  if (loading) {
    return (
      <div className="mb-detail">
        <PageHeader backTo="/maisons" />
        <p className="mb-state">{t('maisons_loading')}</p>
      </div>
    );
  }
  if (error || !maison) {
    return (
      <div className="mb-detail">
        <PageHeader backTo="/maisons" />
        <p className="mb-state">{t('detail_notfound')}</p>
        <p className="mb-detail-backlink">
          <Link to="/maisons">← {t('maisons_title')}</Link>
        </p>
      </div>
    );
  }

  const sub = [maison.address?.city, maison.activity].filter(Boolean).join(' · ');
  const tag = maison.activity || maison.category;
  const img = maison.cover || maison.logo || maison.photos?.[0];
  const { line, postalCode, city } = maison.address ?? { line: '', postalCode: '', city: '' };
  const fullAddress = [line, [postalCode, city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const bookingUrl = maison.contact?.url;
  const phone = maison.contact?.phone;
  const email = maison.contact?.email;
  const website = maison.contact?.url;
  const hours = maison.hours ?? [];

  return (
    <div className="mb-detail">
      <PageHeader backTo="/maisons" />

      <header className="mb-detail-hero">
        <BocuseImage src={img} name={maison.name} className="mb-detail-media" />
        <span className="mb-detail-hero-gradient" aria-hidden="true" />
        {tag && <span className="mb-detail-tag">{tag}</span>}
        <div className="mb-detail-hero-body">
          <h1 className="mb-detail-name">{maison.name}</h1>
          {sub && <p className="mb-detail-sub">{sub}</p>}
        </div>
      </header>

      <main className="mb-detail-main">
        {bookingUrl ? (
          <a className="mb-detail-book" href={bookingUrl} target="_blank" rel="noopener noreferrer">
            {t('detail_reserve')}
          </a>
        ) : (
          <p className="mb-detail-book-soon">{t('detail_reserve_soon')}</p>
        )}

        {maison.description?.text && (
          <p className="mb-detail-desc">{maison.description.text}</p>
        )}

        {fullAddress && (
          <section className="mb-detail-section">
            <h2 className="mb-detail-section-title">{t('detail_address')}</h2>
            <p className="mb-detail-line">{fullAddress}</p>
          </section>
        )}

        {hours.length > 0 && (
          <section className="mb-detail-section">
            <h2 className="mb-detail-section-title">{t('detail_hours')}</h2>
            <ul className="mb-detail-hours">
              {hours.map((h) => (
                <li key={h.dayOfWeek} className="mb-detail-hours-row">
                  <span className="mb-detail-day">{dayLabel(h.dayOfWeek, lang)}</span>
                  <span className="mb-detail-slots">
                    {h.slots.length ? formatSlots(h) : t('detail_closed')}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(phone || email || website) && (
          <section className="mb-detail-section">
            <h2 className="mb-detail-section-title">{t('detail_contact')}</h2>
            {phone && (
              <p className="mb-detail-line">
                <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
              </p>
            )}
            {email && (
              <p className="mb-detail-line">
                <a href={`mailto:${email}`}>{email}</a>
              </p>
            )}
            {website && (
              <p className="mb-detail-line">
                <a href={website} target="_blank" rel="noopener noreferrer">
                  {website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              </p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
